import { MenuItem } from '@mui/material'
import { useState } from 'react'
import { FiUpload } from 'react-icons/fi'
import { TbSubtask, TbTable } from 'react-icons/tb'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { TableBulkUploadDialog } from '../shared/Table/TableBulkUploadDialog'
import { CsvTable, SettingsPageTable } from '../tables/types'
import { Menu } from '../ui/atoms/menu/Menu'

/** Helper function to convert SettingsPageTable to CsvTable */
function settingsPageTableToCsvTable(table: SettingsPageTable): CsvTable {
  return {
    id: table.id,
    name: table.name,
    parentTable: table.parentTable,
    columns: table.columns.map((col) => ({
      id: col.id,
      name: col.name,
      order: col.order,
      type: col.type,
      innerType: col.innerType,
      isNullable: col.isNullable,
      enumOptions: col.enumOptions,
      indexType: col.indexType,
      indexPrefix: col.indexPrefix,
      indexSuffix: col.indexSuffix,
      indexAutogenerate: col.indexAutogenerate,
    })),
    childTables: table.childTables.map((childTable) => ({
      id: childTable.id,
      name: childTable.name,
      columns: childTable.columns.map((col) => ({
        id: col.id,
        name: col.name,
        order: col.order,
        type: col.type,
        innerType: col.innerType,
        isNullable: col.isNullable,
        enumOptions: col.enumOptions,
        indexType: col.indexType,
        indexPrefix: col.indexPrefix,
        indexSuffix: col.indexSuffix,
        indexAutogenerate: col.indexAutogenerate,
      })),
    })),
  }
}

interface UploadDataButton {
  table: SettingsPageTable
}

/**
 * Renders the button for uploading table data
 */
export function UploadDataButton({ table }: UploadDataButton) {
  const [tableForBulkUploadDialog, setTableForBulkUploadDialog] = useState<CsvTable | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const { t } = useTypedTranslation()
  const openTableBulkUploadDialog = (tableId: string) => {
    if (tableId === table.id) {
      setTableForBulkUploadDialog(settingsPageTableToCsvTable(table))
    } else {
      const selectedChildTable = table.childTables.find((childTable) => childTable.id === tableId)
      if (selectedChildTable) {
        // Create a temporary SettingsPageTable for the child table with proper structure
        const childTableAsSettings: SettingsPageTable = {
          ...selectedChildTable,
          parentTable: { __typename: 'Table', id: table.id },
          childTables: [],
          columnGroups: [],
          showRowWorkspace: false,
          hasRowWorkspaceObjects: false,
          organization: table.organization,
          views: [],
          rootWorkspace: {
            __typename: 'Workspace',
            id: '',
            parentObject: { __typename: 'WorkspaceParentObject', name: '' },
          },
        }
        setTableForBulkUploadDialog(settingsPageTableToCsvTable(childTableAsSettings))
      }
    }

    setAnchorEl(null)
  }

  const handleBulkUploadButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    // If there are child tables, open the menu for the user to select which table to upload
    // data for.  Otherwise, just open the bulk upload dialog with
    if (table.childTables.length > 0) {
      setAnchorEl(event.currentTarget)
    } else {
      setTableForBulkUploadDialog(settingsPageTableToCsvTable(table))
      setAnchorEl(null)
    }
  }

  return (
    <div>
      <button
        aria-controls={open ? 'upload-data-table-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        className="flex cursor-pointer items-center justify-center gap-2 rounded px-2.5 py-2 font-normal text-gray-10 transition-opacity duration-300 hover:bg-gray-2"
        id="upload-data-button"
        onClick={handleBulkUploadButtonClick}
      >
        <FiUpload className="size-3" />
        {t('navigation.upload_data')}
      </button>
      <Menu
        MenuListProps={{
          'aria-labelledby': 'upload-data-button',
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        disableScrollLock
        id="add-new-menu"
        onClose={() => setAnchorEl(null)}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          disableRipple
          divider
          onClick={() => {
            openTableBulkUploadDialog(table.id)
          }}
          value={table.id}
        >
          <div className="flex w-full items-center gap-2">
            <div>
              <TbTable />
            </div>
            <div>{table.name}</div>
          </div>
        </MenuItem>
        {table.childTables.map((childTable) => {
          return (
            <MenuItem
              key={childTable.id}
              disableRipple
              onClick={() => {
                openTableBulkUploadDialog(childTable.id)
              }}
              value={childTable.id}
            >
              <div className="flex w-full items-center gap-2">
                <div>
                  <TbSubtask />
                </div>
                <div>{childTable.name}</div>
              </div>
            </MenuItem>
          )
        })}
      </Menu>
      {tableForBulkUploadDialog && (
        <TableBulkUploadDialog
          onClose={() => setTableForBulkUploadDialog(null)}
          open={true}
          table={tableForBulkUploadDialog}
        />
      )}
    </div>
  )
}
